import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, FileText, Download, Eye, CheckCircle2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { generatedFilePath } from '@/components/workflows/docProvenance';
import { DocumentPreviewModal } from '@/components/documents/DocumentPreviewModal';
import { downloadDocumentById } from '@/lib/downloadDocumentById';
import { DocumentSigningControl } from '../DocumentSigningControl';
import { SealApplyCheckbox } from '../SealApplyCheckbox';
import { useAssociationSeal } from '../useAssociationSeal';
import { buildAppointOutputs, OFFICER_FORMATS_APPROVED } from './appointDocumentHtml';
import {
  CONDITIONAL_RESIGNATION_LINE,
  routeAppointment,
  SEQUENCING_TEACHING,
  type AppointWorkflowData,
} from './appointContent';

interface AppointPhase2Props {
  task: any;
  phaseData?: any;
  onPhaseComplete: () => void;
}

export function AppointPhase2({ task, onPhaseComplete }: AppointPhase2Props) {
  const { toast } = useToast();
  const [documents, setDocuments] = useState<any[]>([]);
  const [generating, setGenerating] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<any | null>(null);
  const [unaData, setUnaData] = useState<any | null>(null);
  const [completing, setCompleting] = useState(false);
  const seal = useAssociationSeal(task?.una_id);

  const appointment: AppointWorkflowData = task?.workflow_data?.appointment || {};
  const route = routeAppointment(appointment);

  useEffect(() => {
    loadDocuments();
    loadUna();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id]);

  async function loadUna() {
    if (!task?.una_id) return;
    const { data } = await supabase
      .from('unas')
      .select('name, primary_officer_role')
      .eq('id', task.una_id)
      .maybeSingle();
    if (data) setUnaData(data);
  }

  async function loadDocuments() {
    const { data } = await supabase
      .from('generated_documents')
      .select('*')
      .eq('task_id', task.id)
      .order('generated_at', { ascending: true });
    setDocuments((data || []).filter((d: any) => String(d.merge_field_data?.template_key || '').startsWith('officer_')));
  }

  async function uploadPdfFromHtml(html: string): Promise<{ blob: Blob; size: number }> {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) throw new Error('Please sign in again');
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-pdf-server`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ html, author: unaData?.name || undefined }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'We could not build the file. Please try again.');
    }
    const { pdf: pdfBase64, size } = await response.json();
    const byteCharacters = atob(pdfBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
    return { blob: new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' }), size };
  }

  async function generateAll() {
    setGenerating(true);
    try {
      const outputs = buildAppointOutputs({
        data: appointment,
        associationName: unaData?.name || 'the Association',
        associationOfficerRole: unaData?.primary_officer_role || 'Secretary Treasurer',
        sealEnabled: seal.sealEnabled,
        sealSvg: seal.sealSvg,
      });

      if (outputs.length === 0) {
        toast({ title: 'Nothing to prepare', description: 'Your answers do not call for papers here.' });
        return;
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();
      const createdBy = task.user_id || session?.user?.id || null;

      for (const output of outputs) {
        if (documents.find((d) => d.merge_field_data?.template_key === output.key)) continue;

        const { blob, size } = await uploadPdfFromHtml(output.html);
        const filePath = generatedFilePath(unaData?.name, output.key, task.id);
        const { error: uploadError } = await supabase.storage
          .from('documents')
          .upload(filePath, blob, { contentType: 'application/pdf', upsert: true });
        if (uploadError) throw uploadError;

        const { error: insertError } = await supabase.from('generated_documents').insert({
          task_id: task.id,
          una_id: task.una_id,
          document_name: output.title,
          document_type: 'workflow',
          file_url: filePath,
          file_path: filePath,
          file_size: size,
          status: output.requiresSignature ? 'draft' : 'final',
          custom_content: output.html,
          created_by: createdBy,
          merge_field_data: {
            phase: 'phase_2',
            template_key: output.key,
            is_draft_format: !OFFICER_FORMATS_APPROVED,
            requires_signature: output.requiresSignature,
            signing: output.signing,
            default_ink: output.defaultInk ?? false,
            signing_reason: output.signingReason ?? null,
            sealed: seal.sealEnabled,
            wet_ink_required: output.signing === 'wet_required',
            appointment: appointment as any,
          } as any,
        } as any);
        if (insertError) throw insertError;
      }

      toast({ title: 'Papers ready', description: 'Your papers are on the Record.' });
      await loadDocuments();
    } catch (error: any) {
      console.error('Appointment document generation failed', error);
      toast({
        title: 'We could not finish',
        description: error.message || 'Please try again in a moment.',
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  }

  async function handleComplete() {
    setCompleting(true);
    try {
      onPhaseComplete();
    } finally {
      setCompleting(false);
    }
  }

  return (
    <div className="space-y-6">
      {!OFFICER_FORMATS_APPROVED && (
        <Alert className="border-amber-500/50">
          <AlertDescription className="text-sm">
            These formats are drafts. They are marked DRAFT on every page and are waiting on approval. Do not sign them
            yet.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Prepare the papers</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            This puts your papers on the Record. You only get the pages your answers call for.
          </p>
          {route.generatesNothing ? (
            <Alert>
              <AlertDescription className="text-sm">
                {route.summary} No papers prepare from this path, and that is by design.
              </AlertDescription>
            </Alert>
          ) : (
            <>
            <SealApplyCheckbox
              available={seal.sealAvailable}
              checked={seal.applySeal}
              onChange={seal.setApplySeal}
              id="appoint-apply-seal"
            />
            <Button onClick={generateAll} disabled={generating} className="min-h-11">
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Preparing...
                </>
              ) : (
                'Prepare the papers'
              )}
            </Button>
            </>
          )}
        </CardContent>
      </Card>

      {documents.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Your papers</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {documents.map((doc) => (
              <div key={doc.id} className="rounded-lg border p-3 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{doc.document_name}</span>
                  {doc.merge_field_data?.is_draft_format && <Badge variant="outline">Draft format</Badge>}
                  {doc.merge_field_data?.signing === 'wet_required' ? (
                    <Badge variant="secondary">Sign on paper, blue ink</Badge>
                  ) : doc.merge_field_data?.requires_signature === false ? (
                    <Badge variant="outline">Keep this one with the set</Badge>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPreviewDoc(doc)} className="min-h-11">
                    <Eye className="h-4 w-4 mr-2" />
                    Preview
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="min-h-11"
                    onClick={() => downloadDocumentById(doc.id, { underscoreFilename: true })}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </div>
                {doc.merge_field_data?.requires_signature !== false && (
                  <DocumentSigningControl doc={doc} unaId={task.una_id} sealEnabled={seal.sealAvailable} onChanged={loadDocuments} />
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">After you sign</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p>{SEQUENCING_TEACHING}</p>
          <p>{CONDITIONAL_RESIGNATION_LINE}</p>
          <p>
            Keep the signed originals together as one set, and scan the set to your Record. If banking authority was
            granted, take the signed resolution to the bank so its records match.
          </p>
          <Button onClick={handleComplete} disabled={completing} className="min-h-11">
            {completing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Finishing...
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Mark this done
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {previewDoc && (
        <DocumentPreviewModal open={!!previewDoc} onClose={() => setPreviewDoc(null)} document={previewDoc} />
      )}
    </div>
  );
}

export default AppointPhase2;
